"use client";

import { c as _c } from "react/compiler-runtime";
import type { PublicVenueDetailView } from "@clutch/auth/actions/venues";
import { CallIcon, ChampionIcon, CheckmarkCircle02Icon, Clock01Icon, InformationCircleIcon, Location01Icon, MapsSquare01Icon, SparklesIcon, UserGroupIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getSportConfig } from "@/config/sports";
import { cn } from "@/lib/utils";
import { DAY_LABELS, DAYS_OF_WEEK } from "../_lib/constants";
import { getVenueMapEmbedPreviewUrl } from "../_lib/map-embed";
import { getOpenStatus } from "../_lib/open-status";
type PublicVenueDetailViewProps = {
  venue: PublicVenueDetailView;
};
function groupOperatingHours(operatingHours: Record<string, {
  open: string;
  close: string;
} | null> | null) {
  if (!operatingHours) return [];
  const groups: {
    days: string[];
    hours: string;
  }[] = [];
  let currentGroup: {
    days: string[];
    hours: string;
  } | null = null;
  for (const day of DAYS_OF_WEEK) {
    const hoursObj = operatingHours[day];
    const hoursStr = hoursObj ? `${hoursObj.open} – ${hoursObj.close}` : "Closed";
    if (!currentGroup) {
      currentGroup = {
        days: [DAY_LABELS[day]],
        hours: hoursStr
      };
    } else if (currentGroup.hours === hoursStr) {
      currentGroup.days.push(DAY_LABELS[day]);
    } else {
      groups.push(currentGroup);
      currentGroup = {
        days: [DAY_LABELS[day]],
        hours: hoursStr
      };
    }
  }
  if (currentGroup) {
    groups.push(currentGroup);
  }
  return groups.map(g => {
    let daysLabel = g.days[0];
    if (g.days.length > 1) {
      daysLabel = `${g.days[0]} – ${g.days[g.days.length - 1]}`;
    }
    return {
      daysLabel,
      hours: g.hours
    };
  });
}
export function PublicVenueDetailViewComponent(t0) {
  const $ = _c(82);
  const {
    venue
  } = t0;
  let t1;
  if ($[0] !== venue.mapEmbedUrl) {
    t1 = getVenueMapEmbedPreviewUrl(venue.mapEmbedUrl);
    $[0] = venue.mapEmbedUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const mapEmbedPreviewUrl = t1;
  let t2;
  if ($[2] !== venue.operatingHours) {
    t2 = getOpenStatus(venue.operatingHours);
    $[2] = venue.operatingHours;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const openStatus = t2;
  let t3;
  if ($[4] !== venue.contactPhone) {
    t3 = venue.contactPhone ? `tel:${venue.contactPhone.replace(/\s+/g, "")}` : null;
    $[4] = venue.contactPhone;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const phoneHref = t3;
  let t4;
  if ($[6] !== openStatus) {
    t4 = openStatus && <div className="flex items-center gap-1.5 text-sm sm:hidden"><span aria-hidden={true} className={cn("relative flex h-2 w-2 rounded-full", openStatus.isOpen ? "bg-emerald-500" : "bg-muted-foreground/50")}>{openStatus.isOpen && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />}</span><span className={cn("font-medium", openStatus.isOpen ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground")}>{openStatus.label}</span></div>;
    $[6] = openStatus;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== venue.name) {
    t5 = <h1 className="font-semibold text-3xl tracking-tight md:text-4xl">{venue.name}</h1>;
    $[8] = venue.name;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== venue.address) {
    t6 = venue.address && <div className="flex items-start text-pretty text-base text-muted-foreground"><span>{venue.address}</span></div>;
    $[10] = venue.address;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t5 || $[14] !== t6) {
    t7 = <div className="min-w-0 space-y-2">{t4}{t5}{t6}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== phoneHref || $[17] !== venue.mapUrl) {
    t8 = (phoneHref || venue.mapUrl) && <div className="hidden shrink-0 flex-wrap gap-2 sm:flex">{phoneHref && <Button asChild={true} variant="outline" size="lg"><a href={phoneHref}><HugeiconsIcon icon={CallIcon} className="mr-2 h-4 w-4" />Call</a></Button>}{venue.mapUrl && <Button asChild={true} size="lg"><a href={venue.mapUrl} target="_blank" rel="noreferrer"><HugeiconsIcon icon={MapsSquare01Icon} className="mr-2 h-4 w-4" />Directions</a></Button>}</div>;
    $[16] = phoneHref;
    $[17] = venue.mapUrl;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-5">{t7}{t8}</div>;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== openStatus || $[23] !== venue.spectatorCapacity || $[24] !== venue.sports) {
    t10 = (venue.sports.length > 0 || venue.spectatorCapacity || openStatus) && <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-border/70 border-t pt-4">{venue.sports.length > 0 && <div className="flex items-center gap-1.5">{venue.sports.slice(0, 6).map(_temp)}{venue.sports.length > 6 && <span className="flex h-8 items-center justify-center rounded-lg border border-border/50 bg-accent/40 px-2 font-medium text-muted-foreground text-xs">+{venue.sports.length - 6}</span>}</div>}{venue.spectatorCapacity && <><span aria-hidden={true} className="hidden h-4 w-px bg-border/70 sm:block" /><div className="flex items-center gap-1.5 text-sm"><HugeiconsIcon icon={UserGroupIcon} className="h-4 w-4 text-muted-foreground" /><span className="font-medium text-foreground">{venue.spectatorCapacity.toLocaleString()}</span><span className="text-muted-foreground">seats</span></div></>}{openStatus && <><span aria-hidden={true} className="hidden h-4 w-px bg-border/70 sm:block" /><div className="hidden items-center gap-1.5 text-sm sm:flex"><span aria-hidden={true} className={cn("h-2 w-2 rounded-full", openStatus.isOpen ? "bg-emerald-500" : "bg-muted-foreground/50")} /><span className={cn("font-medium", openStatus.isOpen ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground")}>{openStatus.label}</span></div></>}</div>;
    $[22] = openStatus;
    $[23] = venue.spectatorCapacity;
    $[24] = venue.sports;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t9) {
    t11 = <Card className="border-border/70"><CardHeader className="gap-5">{t9}{t10}</CardHeader></Card>;
    $[26] = t10;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== phoneHref || $[30] !== venue.mapUrl) {
    t12 = (phoneHref || venue.mapUrl) && <Card className="border-border/70 p-4 sm:hidden"><div className="flex gap-2">{phoneHref && <Button asChild={true} variant="outline" size="lg" className="flex-1"><a href={phoneHref}><HugeiconsIcon icon={CallIcon} className="mr-2 h-4 w-4" />Call</a></Button>}{venue.mapUrl && <Button asChild={true} size="lg" className="flex-1"><a href={venue.mapUrl} target="_blank" rel="noreferrer"><HugeiconsIcon icon={MapsSquare01Icon} className="mr-2 h-4 w-4" />Directions</a></Button>}</div></Card>;
    $[29] = phoneHref;
    $[30] = venue.mapUrl;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== venue.coverImageUrl || $[33] !== venue.name) {
    t13 = venue.coverImageUrl && <Card className="gap-0 overflow-hidden border-border/70 py-0"><div className="relative h-[500px] w-full"><Image src={venue.coverImageUrl} alt={`${venue.name} cover`} fill={true} className="object-cover" unoptimized={true} /></div></Card>;
    $[32] = venue.coverImageUrl;
    $[33] = venue.name;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== venue.details) {
    t14 = venue.details && <Card><CardHeader className="flex flex-row items-center gap-2 space-y-0"><HugeiconsIcon icon={InformationCircleIcon} className="h-5 w-5 text-muted-foreground" /><CardTitle>About Venue</CardTitle></CardHeader><CardContent><p className="whitespace-pre-wrap text-foreground text-sm leading-relaxed">{venue.details}</p></CardContent></Card>;
    $[35] = venue.details;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== venue.sports) {
    t15 = venue.sports.length > 0 && <Card><CardHeader className="flex flex-row items-center gap-2 space-y-0"><HugeiconsIcon icon={ChampionIcon} className="h-5 w-5 text-muted-foreground" /><CardTitle>Supported sports</CardTitle></CardHeader><CardContent><div className="grid grid-cols-2 gap-3 sm:grid-cols-3">{venue.sports.map(_temp2)}</div></CardContent></Card>;
    $[37] = venue.sports;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== mapEmbedPreviewUrl || $[40] !== venue.latitude || $[41] !== venue.longitude || $[42] !== venue.mapEmbedUrl || $[43] !== venue.mapUrl) {
    t16 = (venue.latitude || venue.longitude || venue.mapUrl || venue.mapEmbedUrl) && <Card><CardHeader className="flex flex-row items-center gap-2 space-y-0"><HugeiconsIcon icon={MapsSquare01Icon} className="h-5 w-5 text-muted-foreground" /><CardTitle>Map</CardTitle></CardHeader><CardContent>{mapEmbedPreviewUrl && <div className="overflow-hidden rounded-xl border border-border" style={{
          height: "clamp(240px, 42vh, 320px)"
        }}><iframe src={mapEmbedPreviewUrl} title="Venue location" className="block w-full rounded-xl" height={320} style={{
            border: 0,
            height: "100%",
            width: "100%"
          }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen={true} /></div>}</CardContent></Card>;
    $[39] = mapEmbedPreviewUrl;
    $[40] = venue.latitude;
    $[41] = venue.longitude;
    $[42] = venue.mapEmbedUrl;
    $[43] = venue.mapUrl;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== venue.amenities) {
    t17 = venue.amenities && venue.amenities.length > 0 && <Card className="hidden md:flex"><CardHeader className="flex flex-row items-center gap-2 space-y-0"><HugeiconsIcon icon={SparklesIcon} className="h-5 w-5 text-muted-foreground" /><CardTitle>Amenities</CardTitle></CardHeader><CardContent><ul className="flex flex-wrap gap-2">{venue.amenities.map(_temp3)}</ul></CardContent></Card>;
    $[45] = venue.amenities;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t14 || $[48] !== t15 || $[49] !== t16 || $[50] !== t17) {
    t18 = <div className="space-y-6 md:col-span-2">{t14}{t15}{t16}{t17}</div>;
    $[47] = t14;
    $[48] = t15;
    $[49] = t16;
    $[50] = t17;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <CardHeader className="flex flex-row items-center gap-2 space-y-0"><HugeiconsIcon icon={Location01Icon} className="h-5 w-5 text-muted-foreground" /><CardTitle>Address</CardTitle></CardHeader>;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== venue.address) {
    t20 = <p className="font-medium text-foreground text-sm leading-relaxed">{venue.address}</p>;
    $[53] = venue.address;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== venue.mapUrl) {
    t21 = venue.mapUrl && <Button asChild={true} size="lg" variant="outline" className="w-full"><a href={venue.mapUrl} target="_blank" rel="noreferrer"><HugeiconsIcon icon={MapsSquare01Icon} className="mr-2 h-4 w-4" />Open in maps</a></Button>;
    $[55] = venue.mapUrl;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== t20 || $[58] !== t21) {
    t22 = <Card>{t19}<CardContent className="space-y-4">{t20}{t21}</CardContent></Card>;
    $[57] = t20;
    $[58] = t21;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== venue.spectatorCapacity) {
    t23 = venue.spectatorCapacity !== null && venue.spectatorCapacity !== undefined && <Card><CardHeader className="flex flex-row items-center gap-2 space-y-0"><HugeiconsIcon icon={UserGroupIcon} className="h-5 w-5 text-muted-foreground" /><CardTitle>Capacity</CardTitle></CardHeader><CardContent><div className="flex items-center gap-3"><div><p className="font-semibold text-foreground text-xl tracking-tight">{venue.spectatorCapacity.toLocaleString()}</p><p className="mt-0.5 text-muted-foreground text-xs">Maximum spectators</p></div></div></CardContent></Card>;
    $[60] = venue.spectatorCapacity;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== venue.contactPhone) {
    t24 = venue.contactPhone && <Card><CardHeader className="flex flex-row items-center gap-2 space-y-0"><HugeiconsIcon icon={CallIcon} className="h-5 w-5 text-muted-foreground" /><CardTitle>Contact</CardTitle></CardHeader><CardContent><a href={`tel:${venue.contactPhone.replace(/\s+/g, "")}`} className="flex items-center gap-3"><div><p className="font-medium text-primary text-sm leading-none underline underline-offset-4">{venue.contactPhone}</p></div></a></CardContent></Card>;
    $[62] = venue.contactPhone;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  let t25;
  if ($[64] !== venue.operatingHours) {
    t25 = venue.operatingHours && <Card><CardHeader className="flex flex-row items-center gap-2 space-y-0"><HugeiconsIcon icon={Clock01Icon} className="h-5 w-5 text-muted-foreground" /><CardTitle>Operating Hours</CardTitle></CardHeader><CardContent><div className="divide-y divide-border rounded-xl border">{groupOperatingHours(venue.operatingHours).map(_temp4)}</div></CardContent></Card>;
    $[64] = venue.operatingHours;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  let t26;
  if ($[66] !== venue.amenities) {
    t26 = venue.amenities && venue.amenities.length > 0 && <Card className="flex md:hidden"><CardHeader className="flex flex-row items-center gap-2 space-y-0"><HugeiconsIcon icon={SparklesIcon} className="h-5 w-5 text-muted-foreground" /><CardTitle>Amenities</CardTitle></CardHeader><CardContent><ul className="flex flex-wrap gap-2">{venue.amenities.map(_temp5)}</ul></CardContent></Card>;
    $[66] = venue.amenities;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== t22 || $[69] !== t23 || $[70] !== t24 || $[71] !== t25 || $[72] !== t26) {
    t27 = <div className="space-y-6 md:col-span-1">{t22}{t23}{t24}{t25}{t26}</div>;
    $[68] = t22;
    $[69] = t23;
    $[70] = t24;
    $[71] = t25;
    $[72] = t26;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  let t28;
  if ($[74] !== t18 || $[75] !== t27) {
    t28 = <div className="grid gap-6 md:grid-cols-3">{t18}{t27}</div>;
    $[74] = t18;
    $[75] = t27;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] !== t11 || $[78] !== t12 || $[79] !== t13 || $[80] !== t28) {
    t29 = <div className="space-y-6">{t11}{t12}{t13}{t28}</div>;
    $[77] = t11;
    $[78] = t12;
    $[79] = t13;
    $[80] = t28;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  return t29;
}
function _temp5(amenity_0) {
  return <li key={amenity_0} className="inline-flex items-center gap-2 rounded-lg border border-border/50 bg-accent/30 px-2.5 py-1.5 text-foreground text-sm transition-colors hover:bg-accent/50"><HugeiconsIcon icon={CheckmarkCircle02Icon} className="h-4 w-4 shrink-0 text-primary" /><span className="font-medium">{amenity_0}</span></li>;
}
function _temp4(group, i) {
  return <div key={i} className="flex items-center justify-between px-4 py-3 text-sm"><span className="font-medium text-foreground">{group.daysLabel}</span><span className={group.hours === "Closed" ? "font-medium text-muted-foreground" : "font-medium text-foreground"}>{group.hours}</span></div>;
}
function _temp3(amenity) {
  return <li key={amenity} className="inline-flex items-center gap-2 rounded-lg border border-border/50 bg-accent/30 px-2.5 py-1.5 text-foreground text-sm transition-colors hover:bg-accent/50"><HugeiconsIcon icon={CheckmarkCircle02Icon} className="h-4 w-4 shrink-0 text-primary" /><span className="font-medium">{amenity}</span></li>;
}
function _temp2(s_0) {
  const config_0 = getSportConfig(s_0.sportSlug);
  const SportIcon_0 = config_0.icon;
  const meta = s_0.playAreaCount ? `${s_0.playAreaCount} ${s_0.playAreaLabel ?? (s_0.playAreaCount === 1 ? "area" : "areas")}` : null;
  return <div key={s_0.sportSlug} className={cn("flex items-center gap-3 rounded-xl border p-3 transition-colors hover:bg-accent/60", config_0.colorConfig.border)}><div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg", config_0.colorConfig.bgLight)}><SportIcon_0 className={cn("h-5 w-5", config_0.colorConfig.text)} /></div><div className="min-w-0 flex-1"><p className="truncate font-medium text-foreground text-sm leading-tight">{config_0.label}</p>{meta && <p className="mt-0.5 truncate text-muted-foreground text-xs">{meta}</p>}</div></div>;
}
function _temp(s) {
  const config = getSportConfig(s.sportSlug);
  const SportIcon = config.icon;
  return <span key={s.sportSlug} title={config.label} className={cn("flex h-8 w-8 items-center justify-center rounded-lg border", config.colorConfig.bgLight, config.colorConfig.border)}><SportIcon className={cn("h-4 w-4", config.colorConfig.text)} /></span>;
}